import { z } from 'zod';

/**
 * JSON Resume schema (zod strict)
 * Based on https://jsonresume.org/schema/
 * Champs Prisma sans équivalent JSON Resume → droppés
 */

const BasicsSchema = z.object({
  name: z.string(),
  label: z.string().optional(),
  image: z.string().optional(),
  email: z.string().email(),
  phone: z.string().optional(),
  summary: z.string().optional(),
  location: z
    .object({
      city: z.string().optional(),
      region: z.string().optional(),
      countryCode: z.string().optional(),
    })
    .optional(),
  driverLicense: z.string().optional(),
  openToTraining: z.boolean().optional(),
  profiles: z
    .array(
      z.object({
        network: z.string(),
        username: z.string(),
        url: z.string().optional(),
      }),
    )
    .optional(),
});

const WorkSchema = z.object({
  name: z.string(),
  position: z.string().optional(),
  company: z.string().optional(),
  location: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  summary: z.string().optional(),
  highlights: z.array(z.string()).optional(),
});

const EducationSchema = z.object({
  institution: z.string().optional(),
  area: z.string().optional(),
  studyType: z.string().optional(),
  location: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  score: z.string().optional(),
  courses: z.array(z.string()).optional(),
});

const VolunteerSchema = z.object({
  organization: z.string().optional(),
  position: z.string().optional(),
  website: z.string().optional(),
  location: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  summary: z.string().optional(),
  highlights: z.array(z.string()).optional(),
});

const ProjectSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  highlights: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  url: z.string().optional(),
  roles: z.array(z.string()).optional(),
  entity: z.string().optional(),
  type: z.string().optional(),
});

const AwardSchema = z.object({
  title: z.string().optional(),
  date: z.string().optional(),
  awarder: z.string().optional(),
  summary: z.string().optional(),
});

const CertificateSchema = z.object({
  name: z.string().optional(),
  date: z.string().optional(),
  issuer: z.string().optional(),
  url: z.string().optional(),
});

const PublicationSchema = z.object({
  name: z.string().optional(),
  publisher: z.string().optional(),
  releaseDate: z.string().optional(),
  website: z.string().optional(),
  summary: z.string().optional(),
});

const ReferenceSchema = z.object({
  name: z.string().optional(),
  reference: z.string().optional(),
});

const SkillSchema = z.object({
  name: z.string(),
  level: z.string().optional(),
  keywords: z.array(z.string()).optional(),
});

const LanguageSchema = z.object({
  language: z.string(),
  fluency: z.string().optional(),
});

const InterestSchema = z.object({
  name: z.string(),
  keywords: z.array(z.string()).optional(),
});

export const ResumeSchema = z.object({
  basics: BasicsSchema,
  work: z.array(WorkSchema).optional(),
  volunteer: z.array(VolunteerSchema).optional(),
  education: z.array(EducationSchema).optional(),
  awards: z.array(AwardSchema).optional(),
  certificates: z.array(CertificateSchema).optional(),
  publications: z.array(PublicationSchema).optional(),
  skills: z.array(SkillSchema).optional(),
  languages: z.array(LanguageSchema).optional(),
  interests: z.array(InterestSchema).optional(),
  references: z.array(ReferenceSchema).optional(),
  projects: z.array(ProjectSchema).optional(),
});

export type Resume = z.infer<typeof ResumeSchema>;
